'use client';

/**
 * CardServiceFeatureAction — CardServiceFeature의 클릭 상호작용만 담당. client 경계.
 *
 * CardServiceFeature는 서버 컴포넌트로 두고, 팝업 열기·섹션 스크롤처럼
 * 'use client'와 이벤트 핸들러가 필요한 부분만 이 컴포넌트로 분리한다.
 */
import { useCallback, type ReactNode } from 'react';
import { useCommonPopup } from '@/publishing/components/popup/CommonPopupContext';
import Popup from '@/publishing/components/popup/InfoPopup';
import type { PopupData } from '@/publishing/components/popup/InfoPopup';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

interface CardServiceFeatureActionProps {
  children: ReactNode;
  className?: string;
  popup?: PopupData;
  popupId?: string;
  /** GA btnname — 미지정 시 레이어 id 가 그대로 쓰인다. */
  gtmBtnName?: string;
  tag?: string;
  tagColor?: string;
  targetId?: string;
}

export default function CardServiceFeatureAction({
  children,
  className,
  popup,
  popupId,
  gtmBtnName,
  tag,
  tagColor,
  targetId,
}: CardServiceFeatureActionProps) {
  const { openLayer } = useCommonPopup();

  const handleClick = useCallback(() => {
    if (targetId) {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (popup) {
      const layerId = popupId ? `service-feature-${popupId}` : 'service-feature-popup';
      const data = { tag, ...popup, tagBgColor: tagColor };
      openLayer(layerId, close => <Popup isOpen onClose={close} {...data} />, undefined, gtmBtnName);
    }
  }, [openLayer, popup, popupId, gtmBtnName, tag, tagColor, targetId]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      // 시트 row 48 click_navigation — 같은 페이지 안 특정 위치로 스크롤 이동하는 카드.
      // 클릭이 곧 트리거라 집계는 전역 위임 리스너에 맡기고 마크업에 data-* 만 남긴다.
      // popupId 는 스크롤 카드에선 popupKey 가 없어 카드 태그로 떨어진다.
      {...(targetId
        ? gtmClick(GTM_EVENT.NAVIGATION, gtmBtnName ?? popupId ?? targetId)
        : {})}
    >
      {children}
    </button>
  );
}
